<template>
  <OfferingFormPresentational
    v-model:description="description"
    v-model:name="name"
    v-model:max-commit="maxCommit"
    v-model:measure-description="measureDescription"
    v-model:measure-type="measureType"
    v-model:measure-unit="measureUnit"
    v-model:min-commit="minCommit"
    v-model:sku-part="skuPart"
    v-model:has-sphere-content="hasSphereContent"
    :product-id="productId"
    :product-name="productName"
    :product-sku="productSku"
    :error-messages="errorMessages"
    :loading="loading"
    :server-error="serverError"
    @submit="submit"
  />
</template>
<script lang="ts" setup>
import {Detail} from '@/api/errorMessage';
import {ProductOfferingCreateUpdate} from '@/stores/userManagement/userManagement';

const props = defineProps<{
  isUpdateForm: boolean;
  pid: number;
  product?: Product;
  offering?: ProductOffering;
}>();

const emit = defineEmits<{
  'create:offering': [value: ProductOffering];
  'update:offering': [value: ProductOffering];
}>();

const router = useRouter();

// Stores
const productsStore = useProductsStore();

const loading = ref(false);

// Form data
const description = ref('');
const name = ref('');
const maxCommit = ref(0);
const measureDescription = ref('');
const measureType = ref('automatic');
const measureUnit = ref('');
const minCommit = ref(0);
const productId = ref(0);
const productName = ref('');
const skuPart = ref('');
const hasSphereContent = ref(false);
const productSku = computed(() => {
  return props.product !== undefined ? props.product.sku + skuPart.value : '';
});
const errorMessages = ref(new Map());
// Used to tell the input not to use client-side validation
const serverError = ref(false);

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  loading.value = true;
  try {
    const productOffering: ProductOfferingCreateUpdate = {
      product_id: props.pid,
      name: name.value,
      sku_part: skuPart.value,
      measure_type: measureType.value,
      measure_method: measureDescription.value,
      measure_unit: measureUnit.value,
      minimal_commit: minCommit.value,
      maximum_commit: maxCommit.value,
      description: description.value,
      has_sphere_content: hasSphereContent.value,
    };
    if (props.isUpdateForm === false) {
      const o = await productsStore.createProductOffering(productOffering);
      if (o) emit('create:offering', o);
      router.go(-1);
    } else {
      const updateProductOffering: ProductOfferingCreateUpdate = {
        ...productOffering,
        product_id: productId.value,
        id: props.pid,
      };
      const o = await productsStore.updateProductOffering(updateProductOffering);
      if (o) emit('update:offering', o);
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    // Remove .was-validated for server-side validation response.
    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

const load = async () => {
  if (props.product !== undefined) productName.value = props.product.name;
  if (props.isUpdateForm === true && props.offering !== undefined) {
    // Set the form data
    name.value = props.offering.name;
    measureType.value = props.offering.measure_type;
    measureUnit.value = props.offering.measure_unit;
    description.value = props.offering.description;
    measureDescription.value = props.offering.measure_method;
    minCommit.value = props.offering.minimal_commit;
    maxCommit.value = props.offering.maximum_commit;
    productId.value = props.offering.product_id;
    skuPart.value = props.offering.sku_part;
    hasSphereContent.value = props.offering.has_sphere_content;
  }
};

onMounted(() => load());
</script>
<style lang="scss" scoped>
.row {
  margin-bottom: 1rem;
}
</style>
